import {
  CircleX,
  Cloud,
  FileText,
  HeartIcon,
  Link2,
  MessageSquare,
  Pencil,
  Server,
  Star,
  ThumbsDown,
  ThumbsUp,
  User,
} from "lucide-react";
import { useContext, useState, useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Button } from "components/ui/button";
import {
  APPLICATION_TAB_NAMES,
  JOB_APPLICATION_MATCH_STATUS,
  JOB_APPLICATION_MATCH_SUB_STATUS,
  JOB_OPPORTUNITY_STATUSES,
  USER_LIST_VIEWS,
} from "lookup";
import {
  AuthContext,
  JobsContext,
  MatchContext,
  ModalContext,
  NotesContext,
} from "context/providers";
import SvgIcon from "components/base/SvgIcon";
import { SVG_ICON_SURFACE_CLASS } from "components/base/SvgButton";
import { getButtonText } from "components/UserCardList/helpers/userCard";
import RejectionForm from "../UserCardResult/molecules/RejectionForm";
import AppDialog from "components/AppDialog";
import CustomerRateButton from "../UserCardResult/molecules/MatchActionButtons/CustomerRateButton";
import CustomerRateModal from "../UserCardResult/molecules/MatchActionButtons/CustomerRateModal";
import { cn } from "utils";
import { isMatchPreShortlistedStatusType } from "utils/helpers/match";
import { getPlatformProfileUrl, getUserName } from "utils/helpers/users";
import { CTA_TITLES } from "components/UserCardList/constants/ctaTitles";
import { PerfectFitJobModal } from "containers/ModalContainer/modals";
import UserInfoModal from "containers/ModalContainer/modals/UserInfoModal";
import FullProfileDialog from "../shared/FullProfileDialog";
import { useConfirmationEmailNotification } from "components/UserCardList/hooks/useConfirmationEmailNotification";

// Menu entries that a caller may already expose elsewhere, e.g. as an icon
// button next to the menu. Pass them in `hiddenActions` to leave them out.
export const MENU_ACTIONS = {
  MATCH: "match",
  SKIP: "skip",
  FINALIST: "finalist",
  REJECT: "reject",
};

const PRIMARY_ACTION_CLASS =
  "inline-flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50";
const ICON_ONLY_PRIMARY_ACTION_CLASS =
  "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-semibold text-gray-700 transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50";

function MatchActionDropDown({
  row,
  activeTabName,
  collectionKey,
  showPrimaryActions = false,
  hideOverflow = false,
  // MENU_ACTIONS values to leave out of the menu because the caller already
  // exposes them, so the same action is never offered twice.
  hiddenActions = [],
  showFullProfileMenuItem = false,
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [perfectFitJobModalIsOpen, setPerfectFitJobModalIsOpen] =
    useState(false);
  const [matchInfoModalIsOpen, setMatchInfoModalIsOpen] = useState(false);
  const [isFullProfileDialogOpen, setIsFullProfileDialogOpen] = useState(false);

  const { user } = useContext(AuthContext);
  const { jobOpp } = useContext(JobsContext);
  const { updateMatch: contextUpdateMatch, removeMatch: contextRemoveMatch } =
    useContext(MatchContext);
  const { showModal } = useContext(ModalContext);
  const { matchedNotes, match: initMatchNotes } = useContext(NotesContext);

  const { match } = row.original;
  const { applicationId } = match;
  const { id: jobOpportunityId } = jobOpp;
  const hit = row.original;
  const {
    confirmationEmailLabel,
    isSendingConfirmationEmail,
    sendUserNotification,
  } = useConfirmationEmailNotification(hit.email);

  const [isLoading, setIsLoading] = useState({
    [JOB_APPLICATION_MATCH_STATUS.MATCHED]: false,
    [JOB_APPLICATION_MATCH_STATUS.SHORTLISTED]: false,
    [JOB_APPLICATION_MATCH_STATUS.SKIPPED]: false,
    [JOB_APPLICATION_MATCH_STATUS.DELETED]: false,
    [JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST]: false,
    [JOB_APPLICATION_MATCH_SUB_STATUS.NULL]: false,
    [JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER]: false,
    [JOB_APPLICATION_MATCH_STATUS.APPLIED]: false,
  });

  const removeMatch = async () => {
    const confirmRemove = window.confirm(
      `Are you sure you want to delete ${getUserName(row.original)} match?`
    );

    if (confirmRemove) {
      setIsLoading((prev) => ({
        ...prev,
        [JOB_APPLICATION_MATCH_STATUS.DELETED]: true,
      }));

      try {
        await contextRemoveMatch(applicationId, jobOpportunityId);
      } catch (error) {
        console.log("removeExistingMatch error: ", error);
      } finally {
        setIsLoading((prev) => ({
          ...prev,
          [JOB_APPLICATION_MATCH_STATUS.DELETED]: false,
        }));
      }
    }
  };

  const updateMatch = async (args) => {
    if (
      args?.subStatus === JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST &&
      jobOpp.isDMP
    ) {
      showModal({
        type: "finalist",
        applicationId,
        jobOpportunityId,
        matchParams: args,
        match: hit.match,
        collectionKey,
        userId: hit.match?.userId || hit.id,
        isUpdate: true,
      });
      return;
    }

    if (args?.status === JOB_APPLICATION_MATCH_STATUS.MATCHED) {
      // passing the value to the modal so it can be viewed in the modal
      showModal({
        type: "match",
        applicationId,
        jobOpportunityId,
        collectionKey,
        status: args?.status,
        hitStatus: hit.match?.status,
        userId: hit.match?.userId || hit.id,
        isUpdate: true,
      });
      return;
    }

    setIsLoading((prev) => ({
      ...prev,
      [args.status || args.subStatus]: true,
    }));

    try {
      await contextUpdateMatch(applicationId, jobOpportunityId, args);
    } catch (error) {
      console.log("matchAction error: ", error);
    } finally {
      setIsLoading((prev) => ({
        ...prev,
        [args.status || args.subStatus]: false,
      }));
    }
  };

  const isJobStatusActive = jobOpp.status === JOB_OPPORTUNITY_STATUSES.ACTIVE;
  const hasPrimaryMatcher = jobOpp.torcOwner?.username;

  const [showRejectModal, setShowRejectModal] = useState(false);

  const canBeFinalist =
    match.status === JOB_APPLICATION_MATCH_STATUS.APPLIED ||
    match.status === JOB_APPLICATION_MATCH_STATUS.MATCHED;

  const canMarkAccepted = canBeFinalist && user.canManuallyAcceptCandidates;

  const isNotFinalist =
    match?.subStatus !== JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST;

  const { oneOfButtonsLoading, notesCount, canBeMatched, canBeSkipped } =
    useMemo(() => {
      const oneOfButtonsLoading = Object.values(isLoading).some(Boolean);

      const { status, notesCount: matchNotesCount } = match;
      const notesCount =
        matchedNotes[applicationId]?.length ?? matchNotesCount ?? 0;
      const canBeMatched =
        isMatchPreShortlistedStatusType(status) ||
        status === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED ||
        status === JOB_APPLICATION_MATCH_STATUS.INTERESTED;

      const canBeSkipped =
        (activeTabName === APPLICATION_TAB_NAMES.SHORTLISTED ||
          activeTabName === APPLICATION_TAB_NAMES.PRECANDIDATES) &&
        (isMatchPreShortlistedStatusType(status) ||
          status === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED ||
          status === JOB_APPLICATION_MATCH_STATUS.INTERESTED ||
          status === JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK);

      return { oneOfButtonsLoading, notesCount, canBeMatched, canBeSkipped };
    }, [activeTabName, applicationId, isLoading, match, matchedNotes]);

  const showRejectMatched = activeTabName === APPLICATION_TAB_NAMES.MATCHED;

  const isActionHidden = (action) => hiddenActions.includes(action);

  const handleRejectClick = () => {
    setShowRejectModal(true);
  };

  const [settingRate, setSettingRate] = useState(false);

  const updateToMatched = () => {
    if (!match.user?.hasActivePlacements) {
      updateMatch({ status: JOB_APPLICATION_MATCH_STATUS.MATCHED });
      return;
    }

    if (
      window.confirm(
        "Are you sure you want to match this user, even though they have active engagement(s) currently?"
      )
    ) {
      updateMatch({ status: JOB_APPLICATION_MATCH_STATUS.MATCHED });
    }
  };

  const matchButtonTitle = match.user?.hasActivePlacements
    ? `${CTA_TITLES.match} WARNING: User has active engagement(s).`
    : CTA_TITLES.match;
  const disabledMatchTitle = !isJobStatusActive
    ? `${CTA_TITLES.match} This job is not active.`
    : !hasPrimaryMatcher
    ? `${CTA_TITLES.match} This job has no primary matcher.`
    : matchButtonTitle;

  const handleMoreInfoSelect = (e) => {
    e.preventDefault();
    setIsDropdownOpen(false);
    setMatchInfoModalIsOpen(true);
  };

  const handlePerfectFitJobsSelect = (e) => {
    e.preventDefault();
    setIsDropdownOpen(false);
    setPerfectFitJobModalIsOpen(true);
  };

  const primaryActions = (
    <>
      {canBeFinalist && (
        <button
          className={cn(
            PRIMARY_ACTION_CLASS,
            isNotFinalist ? "" : "hover:!fill-red-500 hover:text-red-500"
          )}
          disabled={oneOfButtonsLoading}
          title={CTA_TITLES.finalist}
          onClick={() =>
            updateMatch({
              subStatus: isNotFinalist
                ? JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST
                : JOB_APPLICATION_MATCH_SUB_STATUS.NULL,
            })
          }
        >
          <Star size={20} className={cn(!isNotFinalist && "fill-current")} />
          {getButtonText(isNotFinalist ? "finalist" : "removeFinalist")}
        </button>
      )}

      {canBeMatched && (
        <button
          className={PRIMARY_ACTION_CLASS}
          disabled={
            oneOfButtonsLoading || !isJobStatusActive || !hasPrimaryMatcher
          }
          onClick={updateToMatched}
          title={disabledMatchTitle}
        >
          <SvgIcon
            type={match.user?.hasActivePlacements ? "warning" : "heart"}
            className="w-5 h-5"
          />
          {getButtonText("match")}
        </button>
      )}

      {match.status === JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK && (
        <button
          className={PRIMARY_ACTION_CLASS}
          disabled={
            oneOfButtonsLoading || !isJobStatusActive || !hasPrimaryMatcher
          }
          onClick={() =>
            updateMatch({ status: JOB_APPLICATION_MATCH_STATUS.APPLIED })
          }
          title={CTA_TITLES.apply}
        >
          <HeartIcon size={20} />
          {getButtonText("apply")}
        </button>
      )}

      {canMarkAccepted && (
        <button
          className={PRIMARY_ACTION_CLASS}
          disabled={oneOfButtonsLoading}
          title={CTA_TITLES.accept}
          onClick={() =>
            updateMatch({ status: JOB_APPLICATION_MATCH_STATUS.ACCEPTED })
          }
        >
          <ThumbsUp size={20} />
          {getButtonText(
            "accept",
            isLoading[JOB_APPLICATION_MATCH_STATUS.ACCEPTED]
          )}
        </button>
      )}

      {canBeSkipped && (
        <button
          className={cn(
            showPrimaryActions
              ? ICON_ONLY_PRIMARY_ACTION_CLASS
              : PRIMARY_ACTION_CLASS
          )}
          disabled={oneOfButtonsLoading}
          onClick={() =>
            updateMatch({ status: JOB_APPLICATION_MATCH_STATUS.SKIPPED })
          }
          title={CTA_TITLES.skip}
          aria-label={CTA_TITLES.skip}
        >
          <SvgIcon type="skip" className="w-5 h-5" />
          {!showPrimaryActions
            ? getButtonText(
                "skip",
                isLoading[JOB_APPLICATION_MATCH_STATUS.SKIPPED]
              )
            : null}
        </button>
      )}

      {showRejectMatched && (
        <button
          className={PRIMARY_ACTION_CLASS}
          disabled={oneOfButtonsLoading}
          title={CTA_TITLES.reject}
          onClick={handleRejectClick}
        >
          <ThumbsDown size={20} />
          {getButtonText(
            "reject",
            isLoading[JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER]
          )}
        </button>
      )}
    </>
  );

  return (
    <div className="flex items-center gap-2">
      {showPrimaryActions ? primaryActions : null}

      {!hideOverflow ? (
        <DropdownMenu
          modal={false}
          open={isDropdownOpen}
          onOpenChange={(open) => {
            setIsDropdownOpen(open);
            if (open && applicationId && !matchedNotes[applicationId]) {
              initMatchNotes({ id: applicationId }, jobOpp);
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open action menu</span>
              <SvgIcon type="moreVertical" className={SVG_ICON_SURFACE_CLASS} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!showPrimaryActions &&
              !isActionHidden(MENU_ACTIONS.FINALIST) &&
              canBeFinalist && (
                <DropdownMenuItem
                  title={CTA_TITLES.finalist}
                  onClick={() =>
                    updateMatch({
                      subStatus: isNotFinalist
                        ? JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST
                        : JOB_APPLICATION_MATCH_SUB_STATUS.NULL,
                    })
                  }
                  disabled={oneOfButtonsLoading}
                  className={cn(
                    isNotFinalist
                      ? "hover:!fill-red-500 hover:text-red-500"
                      : ""
                  )}
                >
                  <div className="grid place-items-center h-5 w-5">
                    <Star
                      size={20}
                      className={cn(!isNotFinalist && "fill-current")}
                    />
                  </div>
                  {getButtonText(isNotFinalist ? "finalist" : "removeFinalist")}
                </DropdownMenuItem>
              )}

            {!canBeMatched && (
              <CustomerRateButton
                match={match}
                disabled={oneOfButtonsLoading}
                setSettingRate={setSettingRate}
                viewType={USER_LIST_VIEWS.LIST}
                activeTabName={activeTabName}
              />
            )}

            {!showPrimaryActions &&
              !isActionHidden(MENU_ACTIONS.MATCH) &&
              canBeMatched && (
                <DropdownMenuItem
                  title={matchButtonTitle}
                  disabled={
                    oneOfButtonsLoading ||
                    !isJobStatusActive ||
                    !hasPrimaryMatcher
                  }
                  onClick={() => {
                    if (!match.user?.hasActivePlacements) {
                      updateMatch({
                        status: JOB_APPLICATION_MATCH_STATUS.MATCHED,
                      });
                    } else if (
                      window.confirm(
                        "Are you sure you want to match this user, even though they have active engagement(s) currently?"
                      )
                    ) {
                      updateMatch({
                        status: JOB_APPLICATION_MATCH_STATUS.MATCHED,
                      });
                    }
                  }}
                  type="match"
                >
                  <div className="grid place-items-center h-5 w-5">
                    <SvgIcon
                      type={
                        match.user?.hasActivePlacements ? "warning" : "heart"
                      }
                      className={"!w-full"}
                    />
                  </div>
                  {getButtonText("match")}
                </DropdownMenuItem>
              )}

            {!showPrimaryActions &&
              match.status ===
                JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK && (
                <DropdownMenuItem
                  title={CTA_TITLES.apply}
                  disabled={
                    oneOfButtonsLoading ||
                    !isJobStatusActive ||
                    !hasPrimaryMatcher
                  }
                  onClick={() =>
                    updateMatch({
                      status: JOB_APPLICATION_MATCH_STATUS.APPLIED,
                    })
                  }
                  iconType="heart"
                >
                  <div className="grid place-items-center h-5 w-5">
                    <HeartIcon size={20} />
                  </div>
                  {getButtonText("apply")}
                </DropdownMenuItem>
              )}

            {canMarkAccepted && (
              <DropdownMenuItem
                title={CTA_TITLES.accept}
                disabled={oneOfButtonsLoading}
                onClick={() =>
                  updateMatch({
                    status: JOB_APPLICATION_MATCH_STATUS.ACCEPTED,
                  })
                }
              >
                <div className="grid place-items-center h-5 w-5">
                  <ThumbsUp size={20} />
                </div>

                {getButtonText(
                  "accept",
                  isLoading[JOB_APPLICATION_MATCH_STATUS.ACCEPTED]
                )}
              </DropdownMenuItem>
            )}

            {match.status !== JOB_APPLICATION_MATCH_STATUS.INTERESTED &&
              match.status !==
                JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK && (
                <DropdownMenuItem
                  title={CTA_TITLES.deleteMatch}
                  disabled={oneOfButtonsLoading}
                  onClick={removeMatch}
                >
                  <div className="grid place-items-center h-5 w-5">
                    <CircleX size={20} />
                  </div>

                  {getButtonText(
                    "delete",
                    isLoading[JOB_APPLICATION_MATCH_STATUS.DELETED]
                  )}
                </DropdownMenuItem>
              )}

            {!showPrimaryActions &&
              !isActionHidden(MENU_ACTIONS.SKIP) &&
              canBeSkipped && (
                <DropdownMenuItem
                  title={CTA_TITLES.skip}
                  disabled={oneOfButtonsLoading}
                  onClick={() =>
                    updateMatch({
                      status: JOB_APPLICATION_MATCH_STATUS.SKIPPED,
                    })
                  }
                >
                  <SvgIcon type="skip" className="w-5 h-5" />
                  {getButtonText(
                    "skip",
                    isLoading[JOB_APPLICATION_MATCH_STATUS.SKIPPED]
                  )}
                </DropdownMenuItem>
              )}

            {!showPrimaryActions &&
              !isActionHidden(MENU_ACTIONS.REJECT) &&
              showRejectMatched && (
                <DropdownMenuItem
                  title={CTA_TITLES.reject}
                  disabled={oneOfButtonsLoading}
                  onClick={handleRejectClick}
                >
                  <div className="grid place-items-center h-5 w-5">
                    <ThumbsDown size={20} />
                  </div>

                  {getButtonText(
                    "reject",
                    isLoading[JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER]
                  )}
                </DropdownMenuItem>
              )}

            <DropdownMenuItem
              title={CTA_TITLES.matchNotes}
              onClick={() =>
                showModal({
                  type: "note",
                  title: `Match Notes: ${getUserName(row.original)}`,
                  applicationId: match.applicationId,
                  isAllowedPublicNote: true,
                })
              }
            >
              <div className="grid place-items-center h-5 w-5">
                <FileText size={20} />
              </div>
              Match Notes{!!notesCount ? ` (${notesCount})` : ""}
            </DropdownMenuItem>

            {collectionKey === "match" &&
              !isMatchPreShortlistedStatusType(hit.match?.status) &&
              hit.match?.status !== JOB_APPLICATION_MATCH_STATUS.SHORTLISTED &&
              hit.match?.status !== JOB_APPLICATION_MATCH_STATUS.SKIPPED && (
                <DropdownMenuItem
                  title={CTA_TITLES.candidateQuestions}
                  onClick={() =>
                    showModal({
                      type: "match",
                      applicationId: hit.match?.applicationId,
                      jobOpportunityId: hit.match?.jobOpportunityId,
                      freelancerPitchPrefill: hit.match?.freelancerPitchPrefill,
                      hitStatus: hit.match?.status,
                      freelancerPitch: hit.match?.freelancerPitch,
                      isUpdate: true,
                    })
                  }
                >
                  <MessageSquare size={16} />
                  Candidate Questions
                </DropdownMenuItem>
              )}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              title={CTA_TITLES.editProfile}
              onClick={() =>
                showModal({ type: "userEditProfile", userId: hit.id })
              }
            >
              <Pencil size={16} />
              Edit Profile
            </DropdownMenuItem>

            {user?.canSendAssessments ? (
              <DropdownMenuItem
                title={CTA_TITLES.assessments}
                onClick={() =>
                  showModal({ type: "assessment", userId: hit.id })
                }
              >
                <SvgIcon type="openBook" className="w-4 h-4" />
                Assessments
              </DropdownMenuItem>
            ) : null}

            {hit.isUnclaimed ? (
              <DropdownMenuItem
                title={CTA_TITLES.sendConfirmationEmail}
                disabled={isSendingConfirmationEmail}
                onClick={sendUserNotification}
              >
                <SvgIcon type="simpleEmail" className="w-4 h-4" />
                {confirmationEmailLabel}
              </DropdownMenuItem>
            ) : null}

            <DropdownMenuItem
              title={CTA_TITLES.findPerfectFitJobs}
              onSelect={handlePerfectFitJobsSelect}
            >
              <SvgIcon type="compass" className="w-4 h-4" />
              Find Perfect Fit Jobs
            </DropdownMenuItem>

            {showFullProfileMenuItem ? (
              <DropdownMenuItem
                data-action="view-full-profile"
                title={CTA_TITLES.viewFullProfile}
                onSelect={(event) => {
                  event.preventDefault();
                  setIsDropdownOpen(false);
                  setIsFullProfileDialogOpen(true);
                }}
              >
                <User size={16} />
                Profile
              </DropdownMenuItem>
            ) : null}

            <DropdownMenuItem
              title={CTA_TITLES.addLink}
              onClick={() =>
                showModal({
                  type: "link",
                  userId: hit.id,
                  applicationId: hit?.match?.applicationId,
                  collectionKey,
                })
              }
            >
              <Link2 size={16} />
              Add Link
            </DropdownMenuItem>

            <DropdownMenuItem
              title={CTA_TITLES.moreInfo}
              onSelect={handleMoreInfoSelect}
            >
              <SvgIcon type="info" className="w-4 h-4" />
              More Info
            </DropdownMenuItem>

            <DropdownMenuItem asChild>
              <a
                href={getPlatformProfileUrl(getUserName(hit))}
                target="_blank"
                rel="noreferrer"
                className="w-full"
                title={CTA_TITLES.openPlatformProfile}
              >
                <User size={16} />
                Open Platform Profile
              </a>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <a
                href={`${process.env.REACT_APP_SFDC_BASE_URL}/torcUserRedirect?id=${hit.id}`}
                target="_blank"
                rel="noreferrer"
                className="w-full"
                title={CTA_TITLES.sfdcUser}
              >
                <Cloud size={16} />
                SFDC User
              </a>
            </DropdownMenuItem>

            {activeTabName === APPLICATION_TAB_NAMES.MATCHED && (
              <DropdownMenuItem asChild>
                <a
                  href={`${process.env.REACT_APP_SFDC_BASE_URL}/customFlowOperation?platformmatchid=${hit?.match?.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full"
                  title={CTA_TITLES.sfdcMatch}
                >
                  <Cloud size={16} />
                  SFDC Match
                </a>
              </DropdownMenuItem>
            )}

            {user?.attributes?.groups?.includes(
              process.env.REACT_APP_COGNITO_ADMIN_GROUP
            ) &&
              getUserName(hit) && (
                <DropdownMenuItem asChild>
                  <a
                    href={`https://admin.torc.dev/users/${getUserName(hit)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full"
                    title={CTA_TITLES.adminTool}
                  >
                    <Server size={16} />
                    Admin Tool
                  </a>
                </DropdownMenuItem>
              )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <AppDialog
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        header="Rejection Details"
        className="max-h-[90vh] w-[95%] max-w-none overflow-x-hidden rounded-lg md:w-10/12 lg:w-6/12"
        contentClassName="px-8 pb-8"
      >
        <RejectionForm
          applicationId={applicationId}
          jobOpportunityId={jobOpportunityId}
          isVisible={showRejectModal}
          onClose={() => setShowRejectModal(false)}
        />
      </AppDialog>
      <CustomerRateModal
        match={match}
        disabled={oneOfButtonsLoading}
        setSettingRate={setSettingRate}
        settingRate={settingRate}
      />
      <PerfectFitJobModal
        isDialogOpen={perfectFitJobModalIsOpen}
        setDialogVisibility={setPerfectFitJobModalIsOpen}
        user={hit}
      />
      {showFullProfileMenuItem ? (
        <FullProfileDialog
          open={isFullProfileDialogOpen}
          onOpenChange={setIsFullProfileDialogOpen}
          hit={hit}
          activeTabName={activeTabName}
          collectionKey={collectionKey}
        />
      ) : null}
      <UserInfoModal
        open={matchInfoModalIsOpen}
        onOpenChange={setMatchInfoModalIsOpen}
        match={hit.match || {}}
        user={hit}
      />
    </div>
  );
}

export default MatchActionDropDown;
